import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import type { Application, Criticality } from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../auth/auth';
import {
  COMPONENT_KIND_LABEL,
  COMPONENT_KINDS,
  fromDrafts,
  newDraft,
  toDraft,
  type ComponentDraft,
} from '../../lib/components';

const CRITICALITIES: Criticality[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

/**
 * Registers an application, or edits one — including the components it is made of.
 *
 * Components are a description of the architecture, not a scope: naming a host here makes
 * nothing scannable. They are what the surface map places observed endpoints on, and what it
 * draws the declared connections between.
 */
export function ApplicationDialog({
  application,
  extraComponent,
  onClose,
  onSaved,
}: {
  /** Absent to register a new application. */
  application?: Application;
  /** A component to start with, already filled in — from a surface-map suggestion. */
  extraComponent?: ComponentDraft;
  onClose: () => void;
  onSaved: (application: Application) => void;
}) {
  const queryClient = useQueryClient();
  const editing = application !== undefined;

  const [name, setName] = useState(application?.name ?? '');
  const [description, setDescription] = useState(application?.description ?? '');
  const [criticality, setCriticality] = useState<Criticality>(application?.criticality ?? 'MEDIUM');
  const [drafts, setDrafts] = useState<ComponentDraft[]>(() => [
    ...(application?.components ?? []).map(toDraft),
    ...(extraComponent ? [extraComponent] : []),
  ]);
  const [formErrors, setFormErrors] = useState<string[]>([]);

  /*
   * Fix pull requests let Guardian write to a repository outside the platform, so only a
   * security administrator sees — and the server only accepts — these two settings.
   */
  const { user } = useAuth();
  const mayConfigureFixes = editing && user?.role === 'security-admin';
  const repo = application?.sourceRepository;
  const [repoOwner, setRepoOwner] = useState(repo?.owner ?? '');
  const [repoName, setRepoName] = useState(repo?.repo ?? '');
  const [baseBranch, setBaseBranch] = useState(repo?.baseBranch ?? 'main');
  const [deployEnvironment, setDeployEnvironment] = useState(
    repo?.deploymentEnvironment ?? 'production',
  );
  const [autoFix, setAutoFix] = useState(application?.autoRemediation?.enabled ?? false);
  const repoGiven = repoOwner.trim() !== '' && repoName.trim() !== '';

  const update = (id: string, patch: Partial<ComponentDraft>) =>
    setDrafts((list) => list.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  const remove = (id: string) =>
    setDrafts((list) =>
      list
        .filter((d) => d.id !== id)
        .map((d) => ({ ...d, connectsTo: d.connectsTo.filter((c) => c !== id) })),
    );

  const save = useMutation({
    mutationFn: async () => {
      const result = fromDrafts(drafts);
      if (!result.ok) throw new Error('invalid components');
      const fixes = mayConfigureFixes
        ? {
            ...(repoGiven
              ? {
                  sourceRepository: {
                    provider: 'github' as const,
                    owner: repoOwner.trim(),
                    repo: repoName.trim(),
                    baseBranch: baseBranch.trim(),
                    deploymentEnvironment: deployEnvironment.trim(),
                    ...(repo
                      ? { manifestPath: repo.manifestPath, headersPath: repo.headersPath }
                      : {}),
                  },
                }
              : {}),
            ...(autoFix !== (application?.autoRemediation?.enabled ?? false)
              ? { autoRemediation: autoFix }
              : {}),
          }
        : {};
      const fields = {
        name: name.trim(),
        criticality,
        components: result.components,
        ...(description.trim() ? { description: description.trim() } : {}),
      };
      return application
        ? api.patchApplication(application.id, {
            expectedVersion: application.version,
            ...fields,
            ...fixes,
          })
        : api.createApplication(fields);
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['applications'] });
      await queryClient.invalidateQueries({ queryKey: ['surface', saved.id] });
      onSaved(saved);
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const result = fromDrafts(drafts);
    setFormErrors(result.ok ? [] : result.errors);
    if (result.ok) save.mutate();
  };

  const error = save.error instanceof ApiError ? save.error : null;

  return (
    <div className="modal-scrim" role="presentation" onClick={onClose}>
      <div
        className="card modal modal-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-dialog-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="app-dialog-title" className="panel-title">
          {editing ? `Edit ${application.name}` : 'Register an application'}
        </h2>
        <p className="muted" style={{ fontSize: 13, margin: '0 0 4px' }}>
          Describe what the application is made of. The surface map places what scans observe on
          these components. Naming a host here does not make it scannable — only an approved scope
          does that.
        </p>

        {error ? (
          <p className="login-error" role="alert">
            {error.status === 409
              ? 'Someone else changed this application since you opened it. Close and reopen to see their changes.'
              : error.message}
            {error.fieldErrors?.length ? (
              <span style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
                {error.fieldErrors.map((f) => `${f.path}: ${f.message}`).join(' · ')}
              </span>
            ) : null}
          </p>
        ) : null}
        {formErrors.length > 0 ? (
          <ul className="login-error field-errors" role="alert">
            {formErrors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        ) : null}

        <form onSubmit={submit} className="modal-form">
          <div className="field-row">
            <div className="field">
              <label htmlFor="app-name">Name</label>
              <input
                id="app-name"
                className="input"
                required
                maxLength={120}
                placeholder="Client portal"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="app-criticality">Business criticality</label>
              <select
                id="app-criticality"
                className="input"
                value={criticality}
                onChange={(e) => setCriticality(e.target.value as Criticality)}
              >
                {CRITICALITIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="field">
            <label htmlFor="app-description">Description (optional)</label>
            <textarea
              id="app-description"
              className="input textarea"
              maxLength={2000}
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <fieldset className="scope-group">
            <legend>Components</legend>
            <p className="field-hint">
              An endpoint belongs to the component whose host matches and whose path prefix matches
              longest. Leave hosts and paths empty for parts a scan cannot reach, such as a database
              — they are drawn from the connections you declare.
            </p>

            {drafts.length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>
                No components yet. You can add them now, or after a scan from the surface map's
                suggestions.
              </p>
            ) : null}

            <ol className="component-list">
              {drafts.map((d, index) => (
                <li key={d.id} className="component-edit">
                  <div className="component-edit-head">
                    <span className="mono muted">{index + 1}</span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => remove(d.id)}
                      aria-label={`Remove ${d.name || `component ${index + 1}`}`}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="field-row">
                    <div className="field">
                      <label htmlFor={`c-name-${d.id}`}>Name</label>
                      <input
                        id={`c-name-${d.id}`}
                        className="input"
                        required
                        maxLength={80}
                        placeholder="Customer API"
                        value={d.name}
                        onChange={(e) => update(d.id, { name: e.target.value })}
                      />
                    </div>
                    <div className="field">
                      <label htmlFor={`c-kind-${d.id}`}>Kind</label>
                      <select
                        id={`c-kind-${d.id}`}
                        className="input"
                        value={d.kind}
                        onChange={(e) =>
                          update(d.id, { kind: e.target.value as ComponentDraft['kind'] })
                        }
                      >
                        {COMPONENT_KINDS.map((k) => (
                          <option key={k} value={k}>
                            {COMPONENT_KIND_LABEL[k]}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="field-row">
                    <div className="field">
                      <label htmlFor={`c-hosts-${d.id}`}>Hosts</label>
                      <input
                        id={`c-hosts-${d.id}`}
                        className="input mono"
                        placeholder="portal.example.com"
                        value={d.hosts}
                        onChange={(e) => update(d.id, { hosts: e.target.value })}
                      />
                    </div>
                    <div className="field">
                      <label htmlFor={`c-paths-${d.id}`}>Path prefixes</label>
                      <input
                        id={`c-paths-${d.id}`}
                        className="input mono"
                        placeholder="/api, /rest"
                        value={d.pathPrefixes}
                        onChange={(e) => update(d.id, { pathPrefixes: e.target.value })}
                      />
                    </div>
                  </div>
                  {drafts.length > 1 ? (
                    <fieldset className="component-edges">
                      <legend>Connects to</legend>
                      {drafts
                        .filter((other) => other.id !== d.id)
                        .map((other) => (
                          <label key={other.id} className="check">
                            <input
                              type="checkbox"
                              checked={d.connectsTo.includes(other.id)}
                              onChange={(e) =>
                                update(d.id, {
                                  connectsTo: e.target.checked
                                    ? [...d.connectsTo, other.id]
                                    : d.connectsTo.filter((c) => c !== other.id),
                                })
                              }
                            />
                            {other.name || 'Unnamed component'}
                          </label>
                        ))}
                    </fieldset>
                  ) : null}
                </li>
              ))}
            </ol>

            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => setDrafts((list) => [...list, newDraft()])}
              disabled={drafts.length >= 40}
            >
              Add component
            </button>
          </fieldset>

          {mayConfigureFixes ? (
            <fieldset className="scope-group">
              <legend>Fix pull requests</legend>
              <p className="field-hint">
                Where this application's source lives. With automatic remediation on, Guardian opens
                a pull request for each finding it knows how to fix, waits for a person to merge it
                and for the deploy to land, then re-scans to prove it. Guardian never merges. The
                repository must also be on this environment's allowlist and have the Guardian GitHub
                App installed.
              </p>
              <div className="field-row">
                <div className="field">
                  <label htmlFor="repo-owner">GitHub owner</label>
                  <input
                    id="repo-owner"
                    className="input mono"
                    placeholder="your-org"
                    value={repoOwner}
                    onChange={(e) => setRepoOwner(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="repo-name">Repository</label>
                  <input
                    id="repo-name"
                    className="input mono"
                    placeholder="client-portal"
                    value={repoName}
                    onChange={(e) => setRepoName(e.target.value)}
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field">
                  <label htmlFor="repo-branch">Base branch</label>
                  <input
                    id="repo-branch"
                    className="input mono"
                    value={baseBranch}
                    onChange={(e) => setBaseBranch(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="repo-env">Deployment environment</label>
                  <input
                    id="repo-env"
                    className="input mono"
                    value={deployEnvironment}
                    onChange={(e) => setDeployEnvironment(e.target.value)}
                  />
                </div>
              </div>
              <label className="check">
                <input
                  type="checkbox"
                  checked={autoFix}
                  disabled={!repoGiven}
                  onChange={(e) => setAutoFix(e.target.checked)}
                />
                Automatic remediation — open fix pull requests and verify them after deploy
              </label>
              {application?.autoRemediation?.enabled ? (
                <p className="field-hint">
                  On since {new Date(application.autoRemediation.at).toLocaleDateString()}. Every
                  pull request and re-scan is recorded against the person who turned it on.
                </p>
              ) : null}
            </fieldset>
          ) : null}

          <div className="modal-actions">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={save.isPending || name.trim().length === 0}
            >
              {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Register application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
